import { useCallback, useEffect, useState } from "react";

import {
	readScanSoundMuted,
	scanSoundPreferenceChangeEvent,
	writeScanSoundMuted
} from "~/components/mobile/scanFeedback/scanSoundPreference";
import useSaveLocalPreference from "~/hooks/useSaveLocalPreference";

export interface UseScanSoundPreferenceReturn {
	setSoundMuted: (muted: boolean) => void;
	soundMuted: boolean;
	toggleSoundMuted: () => void;
}

const useScanSoundPreference = (): UseScanSoundPreferenceReturn => {
	const saveLocalPreference = useSaveLocalPreference();
	const persist = useCallback(
		(muted: boolean) => {
			if (!saveLocalPreference(() => writeScanSoundMuted(muted))) return;
			window.dispatchEvent(new Event(scanSoundPreferenceChangeEvent));
		},
		[saveLocalPreference]
	);
	const [soundMuted, setLocalSoundMuted] = useState(readScanSoundMuted);

	useEffect(() => {
		const sync = () => setLocalSoundMuted(readScanSoundMuted());
		window.addEventListener(scanSoundPreferenceChangeEvent, sync);
		window.addEventListener("storage", sync);
		return () => {
			window.removeEventListener(scanSoundPreferenceChangeEvent, sync);
			window.removeEventListener("storage", sync);
		};
	}, []);

	const setSoundMuted = useCallback((muted: boolean) => persist(muted), [persist]);
	const toggleSoundMuted = useCallback(() => persist(!readScanSoundMuted()), [persist]);

	return { setSoundMuted, soundMuted, toggleSoundMuted };
};

export default useScanSoundPreference;
